import { Listing, MinMaxArea, UnitAndMagnitudeMeasure } from '@root/types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PARAMETERS } from '@root/tracking/constants';
import CustomIcon from '@components/shared/CustomIcon';
import FlexibleSizeArea from '@components/shared/FlexibleSizeArea';
import actions from '@store/actions/listingPage';
import DataPoint from './DataPoint';
import css from './BasicInfo.module.less';

const formatCeilingHeightMeasure = (
  ceilingHeight: {
    finished: UnitAndMagnitudeMeasure | null;
    slabToSlab: UnitAndMagnitudeMeasure | null;
  },
  { useNew }: { useNew: boolean },
) => {
  /* eslint-disable-next-line react-hooks/rules-of-hooks */
  const { t } = useTranslation('listing');

  if (useNew) {
    return (
      <span>
        {ceilingHeight.finished ? (
          <div>{t('common:listing.finishedHeight', { height: ceilingHeight.finished })}</div>
        ) : null}
        {ceilingHeight.slabToSlab ? (
          <div>{t('common:listing.slabToSlabHeight', { height: ceilingHeight.slabToSlab })}</div>
        ) : null}
      </span>
    );
  }

  const toFeet = (value: number | undefined) => {
    if (!value) return undefined;
    return Math.round((value / 12) * 10) / 10;
  };

  const toMeters = (value: number | undefined) => {
    if (!value) return undefined;
    return Math.round((value / 30) * 10) / 10;
  };

  const inInches = (measure: UnitAndMagnitudeMeasure | null): boolean => {
    if (!measure) return false;
    return measure?.unit === 'Inches';
  };

  /* eslint-disable-next-line react-hooks/rules-of-hooks */
  const slabToSlabLabel = t('slabToSlab');
  const finishedLabel = t('finished');
  const lengthSymbols = {
    Inches: "'",
    Centimeters: ' m',
  };

  const slabToSlabConvertedValue = inInches(ceilingHeight.slabToSlab)
    ? toFeet(ceilingHeight.slabToSlab?.magnitude)
    : toMeters(ceilingHeight.slabToSlab?.magnitude);
  const finishedConvertedValue = inInches(ceilingHeight.finished)
    ? toFeet(ceilingHeight.finished?.magnitude)
    : toMeters(ceilingHeight.finished?.magnitude);

  const slabToSlabValue = slabToSlabConvertedValue
    ? `${slabToSlabConvertedValue}${
        lengthSymbols[ceilingHeight.slabToSlab!.unit]
      } ${slabToSlabLabel}`
    : '';
  const finishedValue = finishedConvertedValue
    ? `${finishedConvertedValue}${lengthSymbols[ceilingHeight.finished!.unit]} ${finishedLabel}`
    : '';
  const newLineIfNecessary = slabToSlabValue && finishedValue ? <br /> : null;
  return (
    <span>
      {finishedValue}
      {newLineIfNecessary}
      {slabToSlabValue}
    </span>
  );
};

const formatArea = ({ area, flexArea }: { area: string; flexArea: MinMaxArea | null }) => {
  return (
    <span>
      {area}
      {flexArea ? <FlexibleSizeArea minMaxArea={flexArea} /> : null}
    </span>
  );
};

type Props = { listing: Listing };

const BasicInfo = ({ listing }: Props) => {
  const { t } = useTranslation('listing');
  const dispatch = useDispatch();
  const {
    dateAvailable,
    leaseType,
    capacity,
    area,
    availableToday,
    condition,
    slabToSlabCeilingHeightMeasure,
    finishedCeilingHeightMeasure,
    brochures = [],
  } = listing;
  const formatBrochures = () => {
    return (
      <>
        {brochures.map(brochure => (
          <div key={brochure.url} className={css.brochure}>
            <a
              href={brochure.url}
              title={brochure.name}
              onClick={() => {
                dispatch(actions.attachmentClicked(PARAMETERS.listingPage, brochure));
              }}
            >
              <CustomIcon type="download" className={css.attributeIcon} />
              {brochure.name}
            </a>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className={css.basicInfo}>
      <DataPoint<{ area: string; flexArea: MinMaxArea | null }>
        value={{
          area: t('units:area', { area }),
          flexArea: listing.minMaxArea,
        }}
        format={formatArea}
        isAlwaysShown
        label={t('size')}
      />
      {capacity ? (
        <DataPoint
          value={t('common:listing.capacity', { capacity })}
          label={t('estimatedCapacity')}
        />
      ) : null}
      {availableToday ? (
        <DataPoint value={t('common:listing.immediateAvailability')} label={t('available')} />
      ) : (
        <DataPoint value={t('units:date', { date: dateAvailable })} label={t('available')} />
      )}
      <DataPoint
        value={leaseType}
        label={t('leaseType')}
        format={aLeaseType => t(`leaseTypes.${aLeaseType}`)}
      />
      {condition ? (
        <DataPoint
          valueClassName={css.conditionValue}
          value={t(`spaceConditions:${condition}`)}
          label={t('condition')}
        />
      ) : null}
      {finishedCeilingHeightMeasure || slabToSlabCeilingHeightMeasure ? (
        <DataPoint
          value={{
            slabToSlab: slabToSlabCeilingHeightMeasure,
            finished: finishedCeilingHeightMeasure,
          }}
          label={t('ceilingHeight')}
          format={measure =>
            formatCeilingHeightMeasure(measure, {
              useNew: true,
            })
          }
        />
      ) : null}
      {brochures.length > 0 ? (
        <DataPoint
          value={brochures}
          label={t('brochure', { count: brochures.length })}
          format={formatBrochures}
        />
      ) : null}
    </div>
  );
};

export default BasicInfo;
